import { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { ResponsiveContainer, LineChart, BarChart, AreaChart, ComposedChart, Line, Bar, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { useTranslation } from 'react-i18next';

function WeatherTooltip({ active, payload, label, type }) {
    if (!active || !payload?.length) return null

    const formatDate = (dateString) => {
        if (!dateString) return ''
        const parts = dateString.substring(0, 10).split('-')
        if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0].slice(2)}`
        return dateString
    }

    return (
        <Paper elevation={3} sx={{ p: 1, bgcolor: 'rgba(30,30,50,0.95)', color: 'white', borderRadius: 1, fontSize: 12 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#eee' }}>
                {formatDate(label)}
            </Typography>
            {payload.map((value, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: value.color, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: '#ccc' }}>
                        {value.name}: {typeof value.value === 'number' ? value.value.toFixed(1) : value.value}
                        {type === 'temp' && '°C'}
                        {type === 'precipitation' && ' mm'}
                        {type === 'snowfall' && ' cm'}
                        {type === 'wind' && ' km/h'}
                        {type === 'humidity' && '%'}
                    </Typography>
                </Box>
            ))}
        </Paper>
    )
}

function WeatherSubChart({ data, type, t }) {
    const isHourly = data.resolution === 'hourly'

    const chartData = useMemo(() => {
        return data.dates.map((value, index) => {
            const entry = { date: value }
            switch (type) {
                case 'temp':
                    if (isHourly) {
                        entry[t('pages.dashboard.temperature')] = data.temp[index]
                    } else {
                        entry[t('pages.dashboard.max')] = data.temp_max[index]
                        entry[t('pages.dashboard.average')] = data.temp_avg[index]
                        entry[t('pages.dashboard.min')] = data.temp_min[index]
                    }
                    break
                case 'precipitation':
                    entry[t('pages.dashboard.precipitation')] = data.precipitation[index]
                    break
                case 'snowfall':
                    entry[t('pages.dashboard.snowfall')] = data.snowfall[index]
                    break
                case 'wind':
                    if (isHourly) {
                        entry[t('pages.dashboard.speed')] = data.wind_speed[index]
                        entry[t('pages.dashboard.gusts')] = data.wind_gusts[index]
                    } else {
                        entry[t('pages.dashboard.average')] = data.wind_avg[index]
                        entry[t('pages.dashboard.max')] = data.wind_max[index]
                        entry[t('pages.dashboard.gusts')] = data.wind_gusts[index]
                    }
                    break
                case 'humidity':
                    entry[t('pages.dashboard.humidity')] = data.humidity[index]
                    break
                default:
                    break
            }
            return entry
        })
    }, [data, type, isHourly, t])

    const formatXAxis = (value) => {
        if (!value) return ''
        const dateString = String(value)
        if (dateString.includes(' ')) {
            return dateString.split(' ')[1] // time only for hourly
        }
        const parts = dateString.substring(0, 10).split('-')
        if (parts.length === 3) return `${parts[2]}.${parts[1]}`
        return dateString
    }

    const titles = {
        temp: t('pages.dashboard.temperature'),
        precipitation: t('pages.dashboard.precipitation'),
        snowfall: t('pages.dashboard.snowfall'),
        wind: t('pages.dashboard.wind_speed'),
        humidity: t('pages.dashboard.humidity'),
    }

    const units = { temp: '°C', precipitation: 'mm', snowfall: 'cm', wind: 'km/h', humidity: '%' }

    const renderChart = () => {
        const commonXAxis = (
            <XAxis dataKey="date" tickFormatter={formatXAxis} angle={-45} textAnchor="end" height={60} tick={{ fontSize: 10 }} />
        )
        const commonYAxis = <YAxis tick={{ fontSize: 10 }} label={{ value: units[type], angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
        const commonGrid = <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        const commonTooltip = <Tooltip content={<WeatherTooltip type={type} />} />
        const chartMargin = { top: 5, right: 36, bottom: 5, left: 0 }

        switch (type) {
            case 'temp':
                if (isHourly) {
                    return (
                        <LineChart data={chartData} margin={chartMargin}>
                            {commonGrid}{commonXAxis}{commonYAxis}{commonTooltip}
                            <Line type="monotone" dataKey={t('pages.dashboard.temperature')} stroke="#e74c3c" strokeWidth={2} dot={false} isAnimationActive={false} />
                        </LineChart>
                    )
                }
                return (
                    <ComposedChart data={chartData} margin={chartMargin}>
                        {commonGrid}{commonXAxis}{commonYAxis}{commonTooltip}
                        <Area type="monotone" dataKey={t('pages.dashboard.max')} stroke="#e74c3c" fill="rgba(231,76,60,0.15)" strokeWidth={2} dot={false} isAnimationActive={false} />
                        <Area type="monotone" dataKey={t('pages.dashboard.min')} stroke="#c0392b" fill="transparent" strokeWidth={2} strokeDasharray="5 5" dot={false} isAnimationActive={false} />
                        <Line type="monotone" dataKey={t('pages.dashboard.average')} stroke="#e67e22" strokeWidth={2} strokeDasharray="8 4" dot={false} isAnimationActive={false} />
                    </ComposedChart>
                )

            case 'precipitation':
                return (
                    <BarChart data={chartData} margin={chartMargin}>
                        {commonGrid}{commonXAxis}{commonYAxis}{commonTooltip}
                        <Bar dataKey={t('pages.dashboard.precipitation')} fill="rgba(52,152,219,0.7)" isAnimationActive={false} />
                    </BarChart>
                )

            case 'snowfall':
                return (
                    <BarChart data={chartData} margin={chartMargin}>
                        {commonGrid}{commonXAxis}{commonYAxis}{commonTooltip}
                        <Bar dataKey={t('pages.dashboard.snowfall')} fill="rgba(149,165,166,0.8)" isAnimationActive={false} />
                    </BarChart>
                )

            case 'wind':
                return (
                    <LineChart data={chartData} margin={chartMargin}>
                        {commonGrid}{commonXAxis}{commonYAxis}{commonTooltip}
                        {isHourly ? (
                            <>
                                <Line type="monotone" dataKey={t('pages.dashboard.speed')} stroke="#27ae60" strokeWidth={2} dot={false} isAnimationActive={false} />
                                <Line type="monotone" dataKey={t('pages.dashboard.gusts')} stroke="#e74c3c" strokeWidth={1.5} strokeDasharray="5 5" dot={false} isAnimationActive={false} />
                            </>
                        ) : (
                            <>
                                <Line type="monotone" dataKey={t('pages.dashboard.average')} stroke="#27ae60" strokeWidth={2} dot={false} isAnimationActive={false} />
                                <Line type="monotone" dataKey={t('pages.dashboard.max')} stroke="#2ecc71" strokeWidth={2} dot={{ r: 2, fill: '#2ecc71' }} isAnimationActive={false} />
                                <Line type="monotone" dataKey={t('pages.dashboard.gusts')} stroke="#e74c3c" strokeWidth={1.5} strokeDasharray="5 5" dot={false} isAnimationActive={false} />
                            </>
                        )}
                    </LineChart>
                )

            case 'humidity':
                return (
                    <AreaChart data={chartData} margin={chartMargin}>
                        {commonGrid}{commonXAxis}{commonYAxis}{commonTooltip}
                        <Area type="monotone" dataKey={t('pages.dashboard.humidity')} stroke="#8e44ad" fill="rgba(142,68,173,0.1)" strokeWidth={2} dot={false} isAnimationActive={false} />
                    </AreaChart>
                )

            default:
                return null
        }
    }

    return (
        <Paper sx={{ p: 2, pb: 0, mb: 2, userSelect: 'none', '&:last-child': { mb: 0 }, '& :focus': { outline: 'none' }, '& *': { outline: 'none !important' } }}>
            <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center' }}>{titles[type]}</Typography>
            <ResponsiveContainer width="100%" height={250}>
                {renderChart()}
            </ResponsiveContainer>
        </Paper>
    )
}

export default function WeatherCharts({ weatherData, streams }) {

    const { t } = useTranslation()

    if (streams.size === 0) {
        return (
            <Typography color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                {t('pages.dashboard.no_data')}
            </Typography>
        )
    }

    return (
        <Box>
            {streams.has('temp') && <WeatherSubChart data={weatherData} type="temp" t={t} />}
            {streams.has('precipitation') && <WeatherSubChart data={weatherData} type="precipitation" t={t} />}
            {streams.has('snowfall') && <WeatherSubChart data={weatherData} type="snowfall" t={t} />}
            {streams.has('wind') && <WeatherSubChart data={weatherData} type="wind" t={t} />}
            {streams.has('humidity') && <WeatherSubChart data={weatherData} type="humidity" t={t} />}
        </Box>
    )
}
